/**
 * Chat API — thin wrapper over `@wg/api/chat`. All endpoint paths and
 * request/response shaping live in the shared module; this file only
 * injects the embed's instance-scoped auth, baseURL and locale source.
 *
 * Endpoints (see @wg/api/chat):
 *   POST /chat/v1/{sessionId}/message      (JSON or SSE via Accept header)
 *   GET  /chat/v1/{sessionId}/history      (merged history w/ content items)
 *   GET|POST /functions/v1/chat/sessions   (session bootstrap)
 */
import {
  sendChatMessage as sharedSendChatMessage,
  streamChatMessage as sharedStreamChatMessage,
  getInteractionHistory,
  bootstrapChatSession as sharedBootstrapChatSession,
  type StreamChatHandlers,
  type SendChatMessageOptions,
  type SendChatMessageResponse,
  type ChatHistoryMessage,
  type ChatHistoryItem,
  type ChatMessageItem,
  type BootstrapResult,
  type BootstrapOptions,
} from '@wg/api/chat';
import { httpClient, auth } from '../client';
import config from '../../config/env.json';

// Re-export shared types so existing call sites in this app keep working
// without churn. The shared `@wg/api/chat` module is the single source of
// truth for chat request/response shapes.
export type {
  ChatHistoryMessage,
  ChatHistoryItem,
  ChatMessageItem,
  SendChatMessageResponse,
  StreamChatHandlers,
  SendChatMessageOptions,
  BootstrapResult,
  BootstrapOptions,
};

export async function sendChatMessage(
  sessionId: string,
  message: string,
  options: {
    history?: ChatHistoryMessage[];
    language?: string;
  } = {},
): Promise<SendChatMessageResponse> {
  return sharedSendChatMessage(httpClient, sessionId, message, {
    history: options.history,
    language: options.language || 'en-US',
    // The embed's wire shape never carried timeZone — the session keeps the
    // timezone established by the voice path. `null` omits the field.
    timeZone: null,
  });
}

/**
 * Stream a chat message via SSE. The shared `@wg/api/chat` does the parsing;
 * this just curries in the embed-app's auth + baseURL + locale.
 */
export function streamChatMessage(
  sessionId: string,
  message: string,
  handlers: StreamChatHandlers,
  options: SendChatMessageOptions = {},
): Promise<void> {
  return sharedStreamChatMessage(
    {
      baseURL: config.BASE_URL,
      getAuthToken: () => auth.getAuthToken(),
      getSelectedLanguage: () => {
        if (typeof window === 'undefined') return 'en-US';
        try {
          return (
            new URLSearchParams(window.location.search).get('locale') ||
            navigator.language ||
            'en-US'
          );
        } catch {
          return 'en-US';
        }
      },
    },
    sessionId,
    message,
    handlers,
    options,
  );
}

/**
 * Merged history via the FastAPI endpoint (joins interaction_content)
 * instead of the edge-function /functions/v1/chat which only ships
 * transcript strings — that's what made hard-refresh lose every place card,
 * quick-reply, form, etc. See `getInteractionHistory` in @wg/api/chat.
 */
export async function getMergedHistory(
  sessionId: string,
  limit = 50,
  offset = 0,
): Promise<{ messages: ChatHistoryItem[]; hasMore: boolean }> {
  return getInteractionHistory(httpClient, sessionId, limit, offset);
}

/**
 * Find or create a chat session for the current visitor. Shared logic
 * (pinned-agent preference, display overrides) lives in @wg/api/chat.
 */
export async function bootstrapChatSession(
  opts: BootstrapOptions = {},
): Promise<BootstrapResult | null> {
  return sharedBootstrapChatSession(httpClient, opts);
}
